/**
 * Artifacts — API Endpoints Page (Issue #616)
 *
 * List of detected backend route handlers with a detail split-panel.
 * Left: searchable/filterable endpoint list.
 * Right: selected endpoint metadata + matching callers (expandable inline) + code evidence.
 *
 * Issue #636: Each caller row is expandable in place — shows all sibling API calls
 * from the same file/context and linked user interactions, without navigating away.
 *
 * All data from the existing analysis-results endpoint — no AI calls.
 */

import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { ConfidenceBadge } from '../components/shared/ConfidenceBadge';

// Local types (matching ApiRoute / ApiCall from shared, cast from unknown[])
interface ApiRoute {
  id: string;
  methods: string[];
  path: string;
  handlerFunction: string;
  framework: string;
  sourceFile: string;
  sourceLine: number;
  sourceEndLine?: number;
  middleware?: string[];
  pathParams?: Array<{ name: string; type?: string; required: boolean }>;
  requestBodyFields?: string[];
  responseFields?: string[];
  evidence?: Array<{ filePath: string; lineStart: number; lineEnd: number; snippet?: string; matchedPattern?: string }>;
  confidence?: 'deterministic' | 'heuristic';
  confidenceReason?: string;
}

interface ApiCall {
  id: string;
  method: string;
  endpoint: string;
  isDynamicEndpoint: boolean;
  library: string;
  sourceFile: string;
  sourceLine: number;
  sourceContext?: string;
  wrapperFunction?: string;
  evidence?: Array<{ filePath: string; lineStart: number; lineEnd: number; snippet?: string }>;
  confidence?: 'deterministic' | 'heuristic';
  confidenceReason?: string;
}

interface UserInteraction {
  id: string;
  name: string;
  type: string;
  triggerElement: string;
  handlerFunction: string;
  handlerFile: string;
  handlerLine: number;
  component?: string;
  associatedApiCalls: string[];
  confidence?: 'deterministic' | 'heuristic';
  confidenceReason?: string;
}

const METHOD_COLORS: Record<string, string> = {
  GET:    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  POST:   'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  PUT:    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  PATCH:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  WS:     'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  SSE:    'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
};

function MethodBadge({ method }: { method: string }) {
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded font-mono ${METHOD_COLORS[method.toUpperCase()] ?? 'bg-slate-100 text-slate-700'}`}>
      {method.toUpperCase()}
    </span>
  );
}

/** Normalize a route path like /projects/:id/foo into a regex that matches dynamic calls */
function buildRouteRegex(path: string): RegExp {
  const escaped = path
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // escape special chars first
    .replace(/:[^/]+/g, '[^/]+')              // :param → [^/]+
    .replace(/\\\*/g, '.*');                   // * → .*
  return new RegExp(`^${escaped}$`, 'i');
}

/** Normalize a path for matching: strip /api prefix, replace template vars with a fixed token */
function normalizePath(path: string): string {
  return path
    .replace(/^\$\{[^}]+\}/, '')         // strip leading ${BASE_URL} variable
    .replace(/^\/api\//, '/')             // strip /api/ prefix
    .replace(/\$\{[^}]+\}/g, '_p_')      // ${varName} → fixed token
    .replace(/\/+$/, '');                 // strip trailing slashes
}

/** Check if an API call's endpoint matches a route path */
function callMatchesRoute(call: ApiCall, route: ApiRoute): boolean {
  // Normalize call path: strip query string, base URL vars, /api/ prefix, template vars
  const rawCallPath = call.endpoint.split('?')[0].split('#')[0];
  const normalizedCall = normalizePath(rawCallPath);
  const normalizedRoute = normalizePath(route.path);
  // Try exact match on normalized paths
  if (normalizedCall === normalizedRoute) return true;
  // Try route regex (`:param` → `[^/]+`) against normalized call path
  try {
    return buildRouteRegex(normalizedRoute).test(normalizedCall);
  } catch {
    return false;
  }
}

function shortPath(filePath: string): string {
  const parts = filePath.replace(/\\/g, '/').split('/');
  return parts.slice(-2).join('/');
}

// ============= Expanded Caller Section (Issue #636) =============

function ExpandedCallerSection({
  caller,
  allApiCalls,
  interactions,
  projectId,
  endpointLabel,
}: {
  caller: ApiCall;
  allApiCalls: ApiCall[];
  interactions: UserInteraction[];
  projectId: string;
  endpointLabel: string;
}) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const currentUrl = `${pathname}${search}`;
  // All calls from the same file + context (siblings including this one)
  const siblingCalls = useMemo(() => {
    return allApiCalls.filter(
      c => c.sourceFile === caller.sourceFile && (c.sourceContext ?? '') === (caller.sourceContext ?? '')
    );
  }, [allApiCalls, caller]);

  // Interactions that directly reference THIS specific call — not just any call from the same component
  const linkedInteractions = useMemo(() => {
    return interactions.filter(ia => ia.associatedApiCalls.includes(caller.id));
  }, [interactions, caller.id]);

  // Other interactions from the SAME component that trigger different sibling calls (context only)
  // Bug #639: must guard component/file to prevent cross-component bleed
  const siblingCallIds = useMemo(() => new Set(siblingCalls.map(c => c.id)), [siblingCalls]);
  const otherInteractions = useMemo(() => {
    return interactions.filter(ia =>
      !ia.associatedApiCalls.includes(caller.id) &&
      ia.associatedApiCalls.some(callId => siblingCallIds.has(callId)) &&
      (ia.component === caller.sourceContext || ia.handlerFile === caller.sourceFile)
    );
  }, [interactions, caller.id, caller.sourceContext, caller.sourceFile, siblingCallIds]);

  return (
    <div className="mt-1 ml-2 pl-3 border-l-2 border-blue-200 dark:border-blue-800 space-y-3">
      {/* Full file path */}
      <div>
        <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide">File</span>
        <p className="font-mono text-xs text-slate-600 dark:text-slate-300 mt-0.5 break-all">{caller.sourceFile}</p>
      </div>

      {/* All sibling API calls from same file/context */}
      <div>
        <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide">
          All API calls from {caller.sourceContext || shortPath(caller.sourceFile)}
        </span>
        <div className="mt-1 space-y-1">
          {siblingCalls.map(c => (
            <div key={c.id} className={`flex items-center gap-2 px-2 py-1 rounded text-xs ${
              c.id === caller.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
            }`}>
              <MethodBadge method={c.method} />
              <span className="font-mono text-slate-700 dark:text-slate-300">{c.endpoint}</span>
              <span className="text-slate-400 dark:text-slate-500">:{c.sourceLine}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Interactions that directly trigger this call — prominently highlighted (Bug #639) */}
      <div className="rounded-lg border border-purple-200 dark:border-purple-700 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/30 border-b border-purple-200 dark:border-purple-700">
          <svg className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide">
            Triggers this call
          </span>
          <span className="ml-auto text-xs text-purple-500 dark:text-purple-400 font-medium">
            {linkedInteractions.length === 0 ? 'none found' : linkedInteractions.length}
          </span>
        </div>
        {linkedInteractions.length === 0 ? (
          <p className="text-xs text-slate-400 italic px-3 py-2">No direct interaction link detected</p>
        ) : (
          <div className="divide-y divide-purple-100 dark:divide-purple-800">
            {linkedInteractions.map(ia => (
              <Link
                key={ia.id}
                to={`/projects/${projectId}/flows?flowId=${ia.id}`}
                state={{ breadcrumb: [{ label: 'API Endpoints', url: currentUrl }] }}
                className="flex items-start gap-2 px-3 py-2 bg-white dark:bg-gray-800 text-xs hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
              >
                <span className="mt-0.5 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded text-[10px] font-semibold flex-shrink-0 uppercase">
                  {ia.type}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-800 dark:text-slate-100 font-semibold truncate">{ia.name}</div>
                  {ia.triggerElement && (
                    <div className="text-slate-500 dark:text-slate-400 mt-0.5">
                      <span className="font-mono">{ia.triggerElement}</span>
                      {ia.component && <span> in {ia.component}</span>}
                    </div>
                  )}
                </div>
                <svg className="w-3 h-3 text-purple-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Other interactions from the same component — dimmed context (Bug #639) */}
      {otherInteractions.length > 0 && (
        <div>
          <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide">
            Other activity in this component
          </span>
          <div className="mt-1 space-y-0.5 opacity-50">
            {otherInteractions.map(ia => (
              <div key={ia.id} className="flex items-center gap-2 px-2 py-1 rounded text-xs text-slate-500 dark:text-slate-400">
                <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-[10px] font-medium flex-shrink-0">
                  {ia.type}
                </span>
                <span className="truncate">{ia.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link to full caller details */}
      <button
        onClick={() => navigate(
          `/projects/${projectId}/callers?file=${encodeURIComponent(caller.sourceFile)}&context=${encodeURIComponent(caller.sourceContext ?? '')}`,
          { state: { from: { label: endpointLabel, url: currentUrl } } }
        )}
        className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
      >
        View full caller details
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

// ============= Endpoint Detail Panel =============

function StitchBadge({ source }: { source: string }) {
  const colors: Record<string, string> = {
    auto: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
    user: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
    ai: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  };
  return (
    <span
      data-testid="stitch-badge"
      className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${colors[source] ?? 'bg-slate-100 text-slate-600'}`}
    >
      via stitch ({source})
    </span>
  );
}

function EndpointDetailPanel({
  endpoint,
  matchingCallers,
  matchSource,
  allApiCalls,
  interactions,
  projectId,
  stitchCallers,
}: {
  endpoint: ApiRoute;
  matchingCallers: ApiCall[];
  matchSource: 'stitched' | 'regex';
  allApiCalls: ApiCall[];
  interactions: UserInteraction[];
  projectId: string;
  stitchCallers: StitchCallerInfo[] | null;
}) {
  const endpointLabel = `${endpoint.methods.join('/')} ${endpoint.path}`;
  const [callerFilter, setCallerFilter] = useState('');
  const [expandedCallerIds, setExpandedCallerIds] = useState<Set<string>>(new Set());

  // Reset expansion when endpoint changes
  useEffect(() => {
    setExpandedCallerIds(new Set());
    setCallerFilter('');
  }, [endpoint.id]);

  const filteredCallers = useMemo(() => {
    if (!callerFilter.trim()) return matchingCallers;
    const q = callerFilter.toLowerCase();
    return matchingCallers.filter(c =>
      c.endpoint.toLowerCase().includes(q) ||
      c.sourceFile.toLowerCase().includes(q) ||
      (c.sourceContext ?? '').toLowerCase().includes(q)
    );
  }, [matchingCallers, callerFilter]);

  function toggleExpand(callerId: string) {
    setExpandedCallerIds(prev => {
      const next = new Set(prev);
      if (next.has(callerId)) next.delete(callerId);
      else next.add(callerId);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      {/* Endpoint metadata */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5">
        <div className="flex items-center gap-2 mb-3">
          {endpoint.methods.map(m => <MethodBadge key={m} method={m} />)}
          <code className="text-sm font-mono text-slate-800 dark:text-slate-100 font-medium">{endpoint.path}</code>
          <ConfidenceBadge confidence={endpoint.confidence} reason={endpoint.confidenceReason} />
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">Handler</span>
            <p className="font-mono text-slate-700 dark:text-slate-300 mt-0.5">{endpoint.handlerFunction}</p>
          </div>
          <div>
            <span className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">Framework</span>
            <p className="text-slate-700 dark:text-slate-300 mt-0.5 capitalize">{endpoint.framework}</p>
          </div>
          <div className="col-span-2">
            <span className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">File</span>
            <p className="font-mono text-slate-700 dark:text-slate-300 mt-0.5 text-xs break-all">
              {endpoint.sourceFile}
              {endpoint.sourceLine > 0 && <span className="text-slate-400">:{endpoint.sourceLine}</span>}
            </p>
          </div>
          {endpoint.middleware && endpoint.middleware.length > 0 && (
            <div className="col-span-2">
              <span className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">Middleware</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {endpoint.middleware.map(m => (
                  <span key={m} className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-mono text-slate-600 dark:text-slate-300">{m}</span>
                ))}
              </div>
            </div>
          )}
          {endpoint.requestBodyFields && endpoint.requestBodyFields.length > 0 && (
            <div>
              <span className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">Request fields</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {endpoint.requestBodyFields.map(f => (
                  <span key={f} className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 rounded text-xs font-mono text-blue-700 dark:text-blue-300">{f}</span>
                ))}
              </div>
            </div>
          )}
          {endpoint.responseFields && endpoint.responseFields.length > 0 && (
            <div>
              <span className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">Response fields</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {endpoint.responseFields.map(f => (
                  <span key={f} className="px-2 py-0.5 bg-green-50 dark:bg-green-900/30 rounded text-xs font-mono text-green-700 dark:text-green-300">{f}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Evidence */}
      {endpoint.evidence && endpoint.evidence.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Source Evidence</h3>
          <div className="space-y-2">
            {endpoint.evidence.map((ev, i) => (
              <div key={i} className="text-xs font-mono text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 rounded p-2">
                <span className="text-slate-400">{shortPath(ev.filePath)}:{ev.lineStart}</span>
                {ev.snippet && (
                  <pre className="mt-1 text-slate-700 dark:text-slate-300 whitespace-pre-wrap overflow-x-auto">{ev.snippet.trim()}</pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Matching callers — issue #863: stitched is source of truth;
          regex shown only as a fallback flagged "unstitched". */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            API Callers
            <span className="ml-2 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded-full text-xs text-slate-500 dark:text-slate-400">
              {matchingCallers.length}
            </span>
            {matchSource === 'stitched' ? (
              <span className="ml-2 px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 rounded-full text-xs text-teal-600 dark:text-teal-400 font-medium">
                Stitch-resolved
              </span>
            ) : matchingCallers.length > 0 ? (
              <span
                className="ml-2 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 rounded-full text-xs text-amber-700 dark:text-amber-400 font-medium"
                title="No stitches exist for this endpoint yet — falling back to regex matching. Resolve in stitch review for accurate results."
              >
                Unstitched (regex)
              </span>
            ) : null}
          </h3>
          {expandedCallerIds.size > 0 && (
            <button
              onClick={() => setExpandedCallerIds(new Set())}
              className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              Collapse all
            </button>
          )}
        </div>
        {matchingCallers.length > 3 && (
          <input
            type="text"
            placeholder="Filter callers..."
            value={callerFilter}
            onChange={e => setCallerFilter(e.target.value)}
            className="w-full mb-3 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}

        {/* Issue #863: single unified list. The header badge tells
            the user whether this endpoint's callers come from
            stitches or the regex fallback. Per-row provenance pill
            (auto/user/ai) is derived from stitchCallers when present. */}
        {matchingCallers.length === 0 ? (
          <p className="text-xs text-slate-400 dark:text-slate-500">No callers matched for this endpoint.</p>
        ) : (
          <div className="space-y-1">
            {filteredCallers.map(c => {
              const isExpanded = expandedCallerIds.has(c.id);
              const sc = stitchCallers?.find(s => s.callSiteId === c.id);
              return (
                <div key={c.id} className="rounded-lg border border-slate-100 dark:border-gray-700 overflow-hidden">
                  <button
                    onClick={() => toggleExpand(c.id)}
                    className={`w-full flex items-start gap-2 p-2.5 transition-colors text-left ${
                      isExpanded
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <MethodBadge method={c.method} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 font-mono text-xs text-slate-700 dark:text-slate-300">
                        <span className="truncate">{c.endpoint}</span>
                        <ConfidenceBadge confidence={c.confidence} reason={c.confidenceReason} />
                        {sc && <StitchBadge source={sc.source} />}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {c.sourceContext && <span className="font-medium">{c.sourceContext} · </span>}
                        <span className="font-mono">{shortPath(c.sourceFile)}:{c.sourceLine}</span>
                        {c.library && c.library !== 'unknown' && (
                          <span className="ml-1 text-slate-400">via {c.library}</span>
                        )}
                      </div>
                    </div>
                    <svg
                      className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-1 bg-slate-50 dark:bg-slate-800/50">
                      <ExpandedCallerSection
                        caller={c}
                        allApiCalls={allApiCalls}
                        interactions={interactions}
                        projectId={projectId}
                        endpointLabel={endpointLabel}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============= Main Page =============

// Stitch mapping types for issue #864 Phase B.5
interface StitchCallerInfo {
  callSiteId: string;
  filePath: string;
  method: string;
  urlPattern: string;
  enclosingSymbol: string;
  source: string;
}

interface StitchedMappings {
  callerToEndpoint: Record<string, { routeId: string; method: string; path: string; framework: string; confidence: number; source: string }>;
  endpointToCallers: Record<string, StitchCallerInfo[]>;
}

export function ArtifactsEndpointsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fromState = (location.state as { from?: { label: string; url: string } } | null)?.from;

  const [projectName, setProjectName] = useState('');
  const [endpoints, setEndpoints] = useState<ApiRoute[]>([]);
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([]);
  const [interactions, setInteractions] = useState<UserInteraction[]>([]);
  const [stitchedMappings, setStitchedMappings] = useState<StitchedMappings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('endpointId'));
  const [searchQuery, setSearchQuery] = useState('');
  const [methodFilter, setMethodFilter] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        const [project, results] = await Promise.all([
          api.getProject(projectId),
          api.getAnalysisResults(projectId),
        ]);
        if (cancelled) return;
        setProjectName(project.name);
        setEndpoints(results.apiEndpoints as ApiRoute[]);
        setApiCalls(results.apiCalls as ApiCall[]);
        setInteractions((results.interactions as UserInteraction[]) ?? []);

        // Fetch stitch mappings (non-blocking — gracefully ignored on failure)
        api.getStitchedMappings(projectId).then(mappings => {
          if (!cancelled) setStitchedMappings(mappings);
        }).catch(() => { /* stitch data unavailable — use regex fallback */ });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [projectId]);

  function handleSelectEndpoint(id: string | null) {
    setSelectedId(id);
    if (id) setSearchParams({ endpointId: id }, { replace: true });
    else setSearchParams({}, { replace: true });
  }

  const allMethods = useMemo(() => {
    const s = new Set<string>();
    for (const ep of endpoints) {
      for (const m of ep.methods) s.add(m.toUpperCase());
    }
    return [...s].sort();
  }, [endpoints]);

  const filteredEndpoints = useMemo(() => {
    let result = endpoints;
    if (methodFilter) {
      result = result.filter(ep => ep.methods.some(m => m.toUpperCase() === methodFilter));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(ep =>
        ep.path.toLowerCase().includes(q) ||
        ep.handlerFunction.toLowerCase().includes(q) ||
        ep.sourceFile.toLowerCase().includes(q)
      );
    }
    return result;
  }, [endpoints, methodFilter, searchQuery]);

  const selectedEndpoint = useMemo(
    () => filteredEndpoints.find(ep => ep.id === selectedId) ?? null,
    [filteredEndpoints, selectedId]
  );

  // Issue #863 (epic #831): prefer stitch-resolved callers over the
  // regex matcher. The regex stays as a fallback so endpoints with no
  // stitches at all still show their AST-detected callers — but flag
  // those rows as "unstitched" in the UI.
  const matchingCallers = useMemo(() => {
    if (!selectedEndpoint) return [];

    const stitched = stitchedMappings?.endpointToCallers?.[selectedEndpoint.id];
    if (stitched && stitched.length > 0) {
      // Resolve stitched callSiteIds back to full ApiCall rows so the
      // existing detail-row renderer keeps working unchanged.
      const byId = new Map<string, ApiCall>();
      for (const c of apiCalls) byId.set(c.id, c);
      const resolved: ApiCall[] = [];
      for (const sc of stitched) {
        const call = byId.get(sc.callSiteId);
        if (call) resolved.push(call);
      }
      if (resolved.length > 0) return resolved;
    }

    // No stitches for this endpoint — fall back to the regex matcher.
    // This keeps endpoints visible during the migration window while
    // users haven't reviewed every site yet.
    return apiCalls.filter(c => callMatchesRoute(c, selectedEndpoint));
  }, [selectedEndpoint, apiCalls, stitchedMappings]);

  const matchSource: 'stitched' | 'regex' = useMemo(() => {
    if (!selectedEndpoint) return 'regex';
    const stitched = stitchedMappings?.endpointToCallers?.[selectedEndpoint.id];
    return stitched && stitched.length > 0 ? 'stitched' : 'regex';
  }, [selectedEndpoint, stitchedMappings]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link
            to={`/projects/${projectId}`}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div className="flex-1 min-w-0">
            {/* Breadcrumb trail */}
            <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 flex-wrap">
              <Link to={`/projects/${projectId}`} className="hover:text-slate-600 dark:hover:text-slate-300 transition-colors">{projectName}</Link>
              <span>/</span>
              <span className="text-slate-600 dark:text-slate-300 font-medium">API Endpoints</span>
              {fromState && (
                <>
                  <span className="mx-1 text-slate-300 dark:text-slate-600">·</span>
                  <button
                    onClick={() => navigate(fromState.url)}
                    className="inline-flex items-center gap-1 text-blue-500 dark:text-blue-400 hover:underline"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to {fromState.label}
                  </button>
                </>
              )}
            </div>
            <h1 className="text-base font-semibold text-slate-800 dark:text-slate-100 leading-tight mt-0.5">
              API Endpoints
              {!isLoading && (
                <span className="ml-2 text-sm font-normal text-slate-400">({endpoints.length})</span>
              )}
            </h1>
          </div>
        </div>
      </header>

      {isLoading && (
        <div className="flex items-center justify-center flex-1">
          <svg className="animate-spin h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}

      {error && (
        <div className="p-6">
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
        </div>
      )}

      {!isLoading && !error && (
        <div className="flex flex-1 overflow-hidden">
          {/* Left panel — endpoint list */}
          <div className="w-80 xl:w-96 flex-shrink-0 border-r border-slate-200 dark:border-gray-700 flex flex-col overflow-hidden bg-white dark:bg-gray-800">
            {/* Search + filter */}
            <div className="p-3 border-b border-slate-200 dark:border-gray-700 space-y-2">
              <input
                type="text"
                placeholder="Search path, handler, file..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {allMethods.length > 1 && (
                <div className="flex flex-wrap gap-1">
                  <button
                    onClick={() => setMethodFilter(null)}
                    className={`px-2 py-0.5 text-xs rounded font-medium transition-colors ${
                      methodFilter === null
                        ? 'bg-slate-700 text-white dark:bg-slate-200 dark:text-slate-800'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    All
                  </button>
                  {allMethods.map(m => (
                    <button
                      key={m}
                      onClick={() => setMethodFilter(methodFilter === m ? null : m)}
                      className={`px-2 py-0.5 text-xs rounded font-mono font-medium transition-colors ${
                        methodFilter === m
                          ? METHOD_COLORS[m] ?? 'bg-slate-200 text-slate-800'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {filteredEndpoints.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400 dark:text-slate-500">
                  {endpoints.length === 0
                    ? 'No API endpoints detected. Run an analysis first.'
                    : 'No endpoints match your filters.'}
                </div>
              ) : (
                filteredEndpoints.map(ep => (
                  <button
                    key={ep.id}
                    onClick={() => handleSelectEndpoint(ep.id === selectedId ? null : ep.id)}
                    className={`w-full text-left px-3 py-3 border-b border-slate-100 dark:border-gray-700 transition-colors ${
                      selectedId === ep.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500'
                        : 'hover:bg-slate-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {ep.methods.map(m => <MethodBadge key={m} method={m} />)}
                      <ConfidenceBadge confidence={ep.confidence} reason={ep.confidenceReason} />
                    </div>
                    <div className="font-mono text-xs text-slate-800 dark:text-slate-100 truncate">{ep.path}</div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 truncate">
                      {ep.handlerFunction} · {shortPath(ep.sourceFile)}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right panel — detail */}
          <div className="flex-1 overflow-y-auto p-6">
            {selectedEndpoint ? (
              <EndpointDetailPanel
                endpoint={selectedEndpoint}
                matchingCallers={matchingCallers}
                matchSource={matchSource}
                allApiCalls={apiCalls}
                interactions={interactions}
                projectId={projectId!}
                stitchCallers={
                  stitchedMappings?.endpointToCallers?.[selectedEndpoint.id]?.map((sc: any) => ({
                    callSiteId: sc.callSiteId,
                    filePath: sc.filePath,
                    method: sc.method,
                    urlPattern: sc.urlPattern,
                    enclosingSymbol: sc.enclosingSymbol,
                    source: sc.source,
                  })) ?? null
                }
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500 gap-3">
                <svg className="w-10 h-10 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                </svg>
                <p className="text-sm">Select an endpoint to see details and callers</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
